import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@t-one-internal/design-system';
import { CloudArrowUp, CircleNotch } from '@phosphor-icons/react';

export default function SaveReportDialog() {
  const { saveDialogOpen, setSaveDialogOpen, saveReportToAPI, buildQuerySpec } = useReportBuilderStore();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = name.trim().length > 0 && buildQuerySpec() !== null && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const config = await saveReportToAPI(name.trim(), description.trim());
    setSaving(false);
    if (config) {
      setName('');
      setDescription('');
      navigate(`/analytics/report-builder/${config.id}`);
    }
  };

  const handleSaveAndPublish = async () => {
    if (!canSave) return;
    setSaving(true);
    const config = await saveReportToAPI(name.trim(), description.trim());
    setSaving(false);
    if (config) {
      setName('');
      setDescription('');
      navigate(`/analytics/report-builder/${config.id}?publish=true`);
    }
  };

  return (
    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Report</DialogTitle>
          <DialogDescription>Save your current query and visualization settings for later use.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Monthly Headcount by Department"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <DialogFooter>
          <button onClick={() => setSaveDialogOpen(false)}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSaveAndPublish} disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <CloudArrowUp size={14} />}
            Save & Publish
          </button>
          <button onClick={handleSave} disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving && <CircleNotch size={14} className="animate-spin" />}
            Save Report
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
